import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, MotionConfig, useInView, animate } from "framer-motion";
import { BiPlay, BiPause, BiRotateRight } from "react-icons/bi";
import { BsFillClockFill } from "react-icons/bs";
import { FaStar, FaQuoteLeft } from "react-icons/fa";
import BrandButton from "../Components/BrandButton";
import {
  FaHandshakeSimple,
  FaCalendarCheck,
  FaMobileScreenButton,
  FaGlobe,
  FaBullhorn,
  FaRobot,
  FaPhoneVolume,
  FaBolt,
  FaMagnifyingGlass,
  FaPenRuler,
  FaCode,
  FaRocket,
  FaCheck,
  FaXmark,
} from "react-icons/fa6";

import SectionEyebrow from "../Components/SectionEyebrow";

/* -------------------------------------------------------------------------- */
/*  Content (text unchanged)                                                   */
/* -------------------------------------------------------------------------- */

// `count` makes the number in the title animate (count-up) when it scrolls into view.
const FEATURES = [
  {
    icon: FaHandshakeSimple,
    count: { to: 1000, suffix: "+" },
    title: "Happy Clients",
    text: "We've earned the trust of over 1000 satisfied clients by consistently delivering exceptional service, innovative solutions, and measurable results. Their success is our greatest achievement.",
  },
  {
    icon: FaCalendarCheck,
    count: { to: 3000, suffix: "+" },
    title: "Projects Delivered",
    text: "With over 3000 successful projects completed, we bring a proven track record of delivering results across a wide range of industries. We ensure reliability and innovation in every solution we provide.",
  },
  {
    icon: BsFillClockFill,
    plainTitle: "24/7 Support",
    text: "Our dedicated support team is available around the clock to assist you with any questions or issues. Whether it's day or night, we're here to ensure you get the help you need, whenever you need it.",
  },
];

const WHO_TEXT = [
  "MK AI Technologies is a leading provider of digital solutions, delivering unparalleled services to businesses both locally and globally. With a presence in Pakistan, we are poised to bring the best of both worlds to your doorstep. Our team of expert developers and designers specialize in creating custom Android & iOS applications, visually stunning web designs, and comprehensive digital marketing strategies.",
  "With over 10 years of experience in the industry, we have honed our skills to deliver results that consistently exceed our clients' expectations. Our commitment to affordability and reliability sets us apart, allowing businesses of all sizes to access the solutions they need to succeed in today's fast-paced digital landscape. Whether you're looking to expand your online presence, streamline your operations, or reach a wider audience, MK AI Technologies is here to help you succeed.",
  "At  MK AI Technologies, we are dedicated to delivering top-notch, affordable, and reliable solutions to businesses in the realm of mobile and web development. Our experienced team of developers specializes in creating custom Android & iOS applications, as well as providing cutting-edge web development and software development services. We continuously strive to provide our clients with innovative and effective solutions that are tailored to meet their unique needs and requirements.",
];

const CHIPS = [
  { icon: FaMobileScreenButton, label: "Android & iOS Apps" },
  { icon: FaGlobe, label: "Web Development" },
  { icon: FaRobot, label: "AI Agents" },
  { icon: FaPhoneVolume, label: "Twilio IVRs" },
  { icon: FaBolt, label: "GoHighLevel" },
  { icon: FaBullhorn, label: "Digital Marketing" },
];

const PROCESS = [
  {
    icon: FaMagnifyingGlass,
    title: "Discover",
    text: "We learn your goals, users and requirements before writing a single line of code.",
    deliverable: "Project scope and plan",
  },
  {
    icon: FaPenRuler,
    title: "Design",
    text: "Wireframes and UI designs that you review and approve before development starts.",
    deliverable: "Approved UI designs",
  },
  {
    icon: FaCode,
    title: "Build",
    text: "Iterative development with regular updates and demos, so you always see progress.",
    deliverable: "Working demos every sprint",
  },
  {
    icon: FaRocket,
    title: "Launch & Support",
    text: "We release, monitor and keep improving your product, with 24/7 support.",
    deliverable: "Live product and support",
  },
];

const TESTIMONIALS = [
  {
    name: "jogaBlop",
    before:
      "MK AI Technologies truly knocked it out of the park! The team not only delivered beyond expectations but was also ",
    highlight:
      "quick with delivery and excelled at proactive communication, demonstrating a DEEP understanding of the task",
    after: ". Highly recommend for exceptional support & IT work!",
  },
  {
    name: "brendo1025",
    before:
      "I truly appreciate all the help they provided. I had tried with another group that was unsuccessful. ",
    highlight:
      "They were easy to work with, told me exactly what they were going to do and completed it in a timely manner",
    after: ". I highly recommend them.",
  },
  {
    name: "Markharbert",
    before:
      "Amazing to work with. Total professionals and awesome communication every step of the way. I highly recommend them and will send my clients to them for sure. ",
    highlight: "Thanks guys for a great experience.",
    after: " I appreciate the attention to detail and your hard work to get this done.",
  },
];

const GALLERY = [
  { src: "/gallery/gal1.webp", alt: "Focused work at a multi-monitor desk setup", title: "Leading with Focus", sub: "Deep focus, high performance", big: true },
  { src: "/gallery/gal2.webp", alt: "Working at desk", title: "Driving the Vision" },
  { src: "/gallery/gal3.webp", alt: "Team conversation", title: "Team Conversations" },
  { src: "/gallery/gal4.webp", alt: "Team working together", title: "Working Together" },
  { src: "/gallery/gal5.webp", alt: "Foosball game", title: "Game Time" },
  { src: "/gallery/gal6.webp", alt: "Reading and learning", title: "Learning & Growing" },
  { src: "/gallery/gal7.webp", alt: "Team member focused on his work setup", title: "Full Concentration" },
  { src: "/gallery/gal8.webp", alt: "Coffee break", title: "Coffee Breaks" },
  { src: "/gallery/gal9.webp", alt: "Mentoring session", title: "Mentorship" },
];

/* -------------------------------------------------------------------------- */
/*  Animation helpers (same system as before)                                  */
/* -------------------------------------------------------------------------- */

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut", delay },
  }),
};

const Reveal = ({ children, delay = 0, amount = 0.2, className = "" }) => (
  <motion.div
    className={className}
    variants={fadeUp}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount }}
    custom={delay}
  >
    {children}
  </motion.div>
);

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const sectionViewport = { once: false, amount: 0.18 };

const SectionHeader = ({ eyebrow, title, text }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={sectionViewport}
    variants={stagger}
    className="mx-auto max-w-4xl text-center"
  >
    <SectionEyebrow variants={reveal}>{eyebrow}</SectionEyebrow>
    <motion.h2
      variants={reveal}
      className="mt-4 font-display text-3xl font-semibold leading-tight text-slate-900 [text-wrap:balance] sm:text-4xl lg:text-5xl"
    >
      {title}
    </motion.h2>
    {text && (
      <motion.p
        variants={reveal}
        className="mx-auto mt-4 max-w-xl text-base leading-7 text-slate-600"
      >
        {text}
      </motion.p>
    )}
  </motion.div>
);

/* Count-up number: renders the final value for screen readers, animates visually. */
const CountUp = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.8 });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return undefined;
    const controls = animate(0, to, {
      duration: 1.5,
      ease: "easeOut",
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);

  return (
    <span ref={ref} aria-label={`${to}${suffix}`}>
      <span aria-hidden="true">
        {val}
        {suffix}
      </span>
    </span>
  );
};

/* -------------------------------------------------------------------------- */
/*  Gallery image                                                              */
/* -------------------------------------------------------------------------- */

const GalleryImage = ({ g, priority }) => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  // If the image was already cached, onLoad may never fire
  useEffect(() => {
    if (imgRef.current?.complete) setLoaded(true);
  }, []);

  return (
    <figure className="group relative h-full overflow-hidden rounded-2xl bg-slate-100 shadow-[0_1px_2px_rgba(15,23,42,0.06),0_12px_28px_-16px_rgba(15,23,42,0.3)] ring-1 ring-slate-900/10 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_1px_2px_rgba(15,23,42,0.08),0_24px_44px_-18px_rgba(139,74,250,0.45)] motion-reduce:transition-none motion-reduce:hover:translate-y-0">
      {/* Skeleton: visible until the image loads */}
      {!loaded && (
        <div
          aria-hidden="true"
          className="absolute inset-0 animate-pulse bg-gradient-to-br from-slate-100 via-[#EFE7FF] to-slate-100"
        />
      )}

      <img
        ref={imgRef}
        src={g.src}
        alt={g.alt}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className={`h-full w-full object-cover transition-all duration-700 ease-out group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100 ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Dark gradient fades in on hover so the caption is readable */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/10 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 max-md:opacity-100"
      />

      {/* Brand-tinted ring appears on hover */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-2xl ring-2 ring-inset ring-[#8B4AFA]/0 transition-all duration-300 group-hover:ring-[#8B4AFA]/60"
      />

      {/* Caption slides up on hover (always visible on mobile) */}
      <figcaption className="absolute inset-x-0 bottom-0 px-3 pb-3 sm:px-4 sm:pb-4 md:translate-y-3 md:opacity-0 md:transition-all md:duration-300 md:group-hover:translate-y-0 md:group-hover:opacity-100 motion-reduce:md:translate-y-0">
        <span
          aria-hidden="true"
          className="mb-2 block h-0.5 w-8 rounded-full bg-gradient-to-r from-[#8B4AFA] to-[#bb94ff] transition-all duration-300 group-hover:w-14"
        />
        <p className={`font-display font-semibold text-white ${g.big ? "text-base sm:text-xl" : "text-sm"}`}>
          {g.title}
        </p>
        {g.sub && <p className="mt-0.5 hidden text-sm text-white/80 sm:block">{g.sub}</p>}
      </figcaption>
    </figure>
  );
};

/* -------------------------------------------------------------------------- */
/*  Process                                 */
/* -------------------------------------------------------------------------- */

const trackViewport = { once: true, amount: 0.3 };

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut", delay: 0.2 + i * 0.15 },
  }),
};

const ProcessTrack = () => (
  <div className="relative">
    {/* Desktop track: draws left to right through the centre of each node */}
    <div
      aria-hidden="true"
      className="absolute inset-x-0 top-6 hidden h-0.5 -translate-y-1/2 bg-slate-200 lg:block"
    >
      <motion.span
        className="block h-full origin-left bg-gradient-to-r from-[#8B4AFA] to-[#bb94ff]"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={trackViewport}
        transition={{ duration: 1.2, ease: "easeInOut" }}
      />
    </div>

    {/* Mobile / tablet track: vertical */}
    <div
      aria-hidden="true"
      className="absolute bottom-6 left-6 top-6 w-0.5 -translate-x-1/2 bg-slate-200 lg:hidden"
    >
      <motion.span
        className="block h-full origin-top bg-gradient-to-b from-[#8B4AFA] to-[#bb94ff]"
        initial={{ scaleY: 0 }}
        whileInView={{ scaleY: 1 }}
        viewport={trackViewport}
        transition={{ duration: 1.2, ease: "easeInOut" }}
      />
    </div>

    <ol className="relative grid gap-8 lg:grid-cols-4 lg:gap-6">
      {PROCESS.map((step, i) => {
        const Icon = step.icon;
        return (
          <li key={step.title} className="group relative flex pl-16 lg:flex-col lg:pl-0">
            {/* Node */}
            <motion.div
              className="absolute left-0 top-0 flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#8B4AFA] bg-white text-lg text-[#8B4AFA] transition-colors duration-300 group-hover:bg-[#8B4AFA] group-hover:text-white lg:static"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={trackViewport}
              transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.15 + i * 0.25 }}
            >
              <Icon aria-hidden="true" />
            </motion.div>

            {/* Card (motion wrapper handles entry, inner div handles hover) */}
            <motion.div
              className="flex w-full flex-1 lg:mt-6"
              variants={cardVariants}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={trackViewport}
            >
              <div className="flex w-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 group-hover:-translate-y-1 group-hover:border-[#8B4AFA]/40 group-hover:shadow-lg group-hover:shadow-[#8B4AFA]/10 motion-reduce:group-hover:translate-y-0">
                <span
                  aria-hidden="true"
                  className="font-display text-sm font-semibold tracking-wider text-[#8B4AFA]"
                >
                  0{i + 1}
                </span>
                <h3 className="mt-2 font-display text-xl font-semibold text-slate-900">
                  {step.title}
                </h3>
                <p className="mt-2 flex-1 text-base leading-7 text-slate-600">{step.text}</p>

                <p className="mt-5 flex items-center gap-2.5 border-t border-slate-100 pt-4 text-sm font-medium text-slate-700">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#8B4AFA]/10 text-[10px] text-[#6e3fc7] transition-colors duration-300 group-hover:bg-[#8B4AFA] group-hover:text-white">
                    <FaCheck aria-hidden="true" />
                  </span>
                  {step.deliverable}
                </p>
              </div>
            </motion.div>
          </li>
        );
      })}
    </ol>
  </div>
);



const About = () => {
  const videoRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isEnded, setIsEnded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isEnded) {
      video.currentTime = 0;
      setIsEnded(false);
      video.play().catch(() => {});
    } else if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    setProgress((video.currentTime / video.duration) * 100);
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="mk-page relative overflow-x-clip text-slate-900">
        {/* ============================== HERO ============================== */}
        <section className="relative px-4 pb-44 pt-40 text-center sm:px-6 sm:pb-56 sm:pt-44">
          {/* Soft brand glow for depth */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-28 -z-10 h-72 w-[40rem] max-w-full -translate-x-1/2 rounded-full bg-[#8B4AFA]/10 blur-3xl"
          />
          <div className="mx-auto max-w-4xl">
            <motion.p
              className="inline-flex items-center gap-2 rounded-md border border-[#8B4AFA]/20 bg-white/80 px-3 py-1.5 text-xs font-semibold uppercase text-[#6e3fc7]"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0}
            >
              <span className="relative flex h-1.5 w-1.5">
                <span
                  aria-hidden="true"
                  className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#8B4AFA]/60 motion-reduce:hidden"
                />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#8B4AFA]" />
              </span>
              About Us
            </motion.p>

            <motion.h1
              className="mt-6 font-display text-4xl font-semibold leading-[1.04] text-slate-900 [text-wrap:balance] sm:text-5xl lg:text-6xl"
              initial={{ y: 20 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              Building modern, scalable solutions that drive{" "}
              <span className="relative isolate inline-block px-1 text-[#6e3fc7]">
                <motion.span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-[0.12em] -z-10 h-[0.3em] origin-left bg-[#E9D5FF]"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.7, ease: "easeOut", delay: 0.5 }}
                />
                real results
                <motion.span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-px origin-left bg-[#8B4AFA]"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.7, ease: "easeOut", delay: 0.6 }}
                />
              </span>
            </motion.h1>

            <motion.p
              className="mx-auto mt-6 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg"
              initial={{ y: 20 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
            >
              We are passionate about building modern, scalable, and impactful solutions that drive
              real-world results. Our team is made up of creative thinkers, developers, and
              strategists who are committed to excellence.
            </motion.p>

            <motion.div
              className="mt-9 flex flex-col items-stretch justify-center gap-4 pb-1.5 pr-1.5 sm:flex-row sm:items-center"
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0.38}
            >
              <BrandButton as={Link} to="/contact" className="w-full sm:w-auto">
                Share Your Requirements
              </BrandButton>
              <BrandButton as={Link} to="/contact" variant="secondary" className="w-full sm:w-auto">
                Hire Our Experts
              </BrandButton>
            </motion.div>
          </div>
        </section>

        {/* ============================== VIDEO ============================== */}
        <section className="relative z-10 -mt-32 px-4 sm:-mt-44 sm:px-6 lg:px-8">
          <motion.div
            className="relative mx-auto max-w-5xl"
            initial={{ y: 40, scale: 0.97 }}
            whileInView={{ y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-4 -bottom-4 top-12 -z-10 rounded-[2.5rem] bg-[#8B4AFA]/10 blur-2xl"
            />
            <div className="rounded-[1.5rem] border border-transparent p-1.5 shadow-[0_24px_60px_-24px_rgba(76,29,149,0.25)] [background:linear-gradient(#fff,#fff)_padding-box,linear-gradient(to_bottom,rgba(139,74,250,0.45),rgba(226,232,240,0.9)_50%,rgba(139,74,250,0.15))_border-box] sm:rounded-[1.75rem] sm:p-2">
              <div
                id="team-video"
                className="group relative aspect-video scroll-mt-32 overflow-hidden rounded-[1.125rem] bg-slate-900 ring-1 ring-slate-900/10 sm:rounded-[1.25rem]"
              >
                {!videoError ? (
                  <video
                    ref={videoRef}
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                    src="/mk-team-new.mp4"
                    poster="/mk-team.webp"
                    preload="none"
                    playsInline
                    controls={false}
                    onTimeUpdate={handleTimeUpdate}
                    onPlay={() => {
                      setIsPlaying(true);
                      setIsEnded(false);
                    }}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => {
                      setIsPlaying(false);
                      setIsEnded(true);
                    }}
                    onError={() => setVideoError(true)}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center px-6 text-center text-sm text-white/70">
                    Video unavailable right now. Please check back later.
                  </div>
                )}

                {!videoError && (
                  <>
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent" />
                    <span className="pointer-events-none absolute left-4 top-4 z-10 inline-flex items-center gap-2 rounded-md bg-white px-3 py-1.5 text-xs font-semibold uppercase text-[#6e3fc7] shadow-md">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#8B4AFA]" />
                      Meet the team
                    </span>
                    <div className="absolute bottom-0 left-0 h-1.5 w-full bg-white/20">
                      <div
                        className="h-full bg-[#8B4AFA] transition-all duration-200"
                        style={{ width: `${progress}%` }}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={togglePlayback}
                      aria-label={isEnded ? "Replay video" : isPlaying ? "Pause video" : "Play video"}
                      className={`absolute inset-0 flex items-center justify-center transition-colors duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-[#8B4AFA] ${
                        isPlaying ? "bg-transparent hover:bg-black/25" : "bg-black/20 hover:bg-black/30"
                      }`}
                    >
                      <span
                        className={`flex flex-col items-center gap-4 transition-opacity duration-300 ${
                          isPlaying ? "opacity-0 group-hover:opacity-100" : "opacity-100"
                        }`}
                      >
                        <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-[#8B4AFA] text-white shadow-lg shadow-black/25 ring-2 ring-white/70 transition-transform duration-300 group-hover:scale-105 sm:h-[4.5rem] sm:w-[4.5rem]">
                          {/* Pulse ring draws attention to the play button */}
                          {!isPlaying && (
                            <span
                              aria-hidden="true"
                              className="absolute inset-0 animate-ping rounded-full bg-[#8B4AFA]/40 motion-reduce:hidden"
                            />
                          )}
                          <span className="relative">
                            {isEnded ? (
                              <BiRotateRight className="h-9 w-9" />
                            ) : isPlaying ? (
                              <BiPause className="h-10 w-10" />
                            ) : (
                              <BiPlay className="ml-1 h-10 w-10" />
                            )}
                          </span>
                        </span>
                        {!isPlaying && (
                          <span className="rounded-full bg-black/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur">
                            {isEnded ? "Watch again" : "Watch our team"}
                          </span>
                        )}
                      </span>
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </section>

        {/* ============================= FEATURES ============================ */}
        <section className="px-4 py-24 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <SectionHeader
              eyebrow="Why MK AI"
              title="Why Clients Choose Us"
              text="Real experience, proven results, and support when you need it."
            />

            <div className="mt-14 grid gap-6 md:grid-cols-3">
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <Reveal key={f.text.slice(0, 20)} delay={i * 0.1} amount={0.3}>
                    <div className="group relative h-full overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#8B4AFA]/40 hover:shadow-xl hover:shadow-[#8B4AFA]/10">
                      {/* Top accent line grows on hover */}
                      <span
                        aria-hidden="true"
                        className="absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-gradient-to-r from-[#8B4AFA] to-[#bb94ff] transition-transform duration-500 group-hover:scale-x-100"
                      />
                      <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#8B4AFA]/10 text-2xl text-[#8B4AFA] transition-all duration-300 group-hover:scale-105 group-hover:bg-[#8B4AFA] group-hover:text-white">
                        <Icon aria-hidden="true" />
                      </span>
                      <h3 className="mt-6 font-display text-xl font-semibold text-slate-900">
                        {f.count ? (
                          <>
                            <span className="text-[#6e3fc7]">
                              <CountUp to={f.count.to} suffix={f.count.suffix} />
                            </span>{" "}
                            {f.title}
                          </>
                        ) : (
                          f.plainTitle
                        )}
                      </h3>
                      <p className="mt-3 text-base leading-7 text-slate-600">{f.text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============================== WHO WE ARE ========================= */}
        <section className="border-y border-slate-200/70 bg-[#FAF8FF] px-4 py-24 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <SectionHeader
              eyebrow="Who We Are"
              title="The team and philosophy behind MK AI Technologies"
            />

            <Reveal className="mt-14" amount={0.1}>
              <div className="relative grid overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-2xl shadow-slate-900/10 transition-[border-color,box-shadow] duration-300 hover:border-[#8B4AFA]/40 hover:shadow-[#8B4AFA]/20 lg:grid-cols-5">
                <div className="absolute inset-x-0 top-0 z-10 h-1 bg-gradient-to-r from-[#8B4AFA] to-[#bb94ff]" />

                <div className="order-2 space-y-5 p-6 sm:p-10 lg:order-1 lg:col-span-3 lg:p-12">
                  {WHO_TEXT.map((para) => (
                    <p key={para.slice(0, 24)} className="text-base leading-8 text-slate-600">
                      {para}
                    </p>
                  ))}
                </div>

                <div className="order-1 flex flex-col border-b border-slate-200/70 bg-[#faf8ff] p-4 sm:p-8 lg:order-2 lg:col-span-2 lg:border-b-0 lg:border-l">
                  <div className="group aspect-[4/3] overflow-hidden rounded-2xl shadow-md ring-1 ring-gray-900/10">
                    <img
                      src="/contact-image.webp"
                      alt="MK AI Technologies team at work"
                      width="700"
                      height="424"
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:transition-none"
                    />
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-3 min-[400px]:grid-cols-2">
                    {CHIPS.map((c) => {
                      const Icon = c.icon;
                      return (
                        <div
                          key={c.label}
                          className="group flex min-w-0 items-center gap-3 rounded-xl border border-[#8B4AFA]/15 bg-white p-3 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#8B4AFA]/40 hover:shadow-md hover:shadow-[#8B4AFA]/10 sm:p-4"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#8B4AFA]/10 text-sm text-[#8B4AFA] transition-colors duration-300 group-hover:bg-[#8B4AFA] group-hover:text-white">
                            <Icon aria-hidden="true" />
                          </span>
                          <span className="min-w-0 break-words text-sm font-semibold text-slate-800">
                            {c.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ============================== PROCESS ============================ */}
        <section className="px-4 py-24 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <SectionHeader
              eyebrow="Our Process"
              title="How we work with you"
              text="A clear, simple process so you always know what happens next."
            />

            <div className="mt-14">
              <ProcessTrack />
            </div>
          </div>
        </section>

        {/* ============================= TESTIMONIALS ======================== */}
        <section className="border-y border-slate-200/70 bg-[#FAF8FF] px-4 py-24 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <SectionHeader
              eyebrow="Testimonials"
              title="Here's what our customers said"
              text="Genuine feedback from real clients who have experienced the value, support, and results we consistently deliver."
            />

            <div className="mt-14 grid gap-6 lg:grid-cols-3">
              {TESTIMONIALS.map((t, i) => (
                <Reveal key={t.name} delay={i * 0.1} amount={0.2}>
                  <figure className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#8B4AFA]/40 hover:shadow-xl hover:shadow-[#8B4AFA]/10">
                    <div className="flex items-center justify-between">
                      <div className="flex gap-1 text-[#8B4AFA]" role="img" aria-label="5 out of 5 stars">
                        {[...Array(5)].map((_, n) => (
                          <FaStar
                            key={n}
                            aria-hidden="true"
                            className="h-4 w-4 transition-transform duration-300 group-hover:scale-110"
                            style={{ transitionDelay: `${n * 40}ms` }}
                          />
                        ))}
                      </div>
                      <FaQuoteLeft
                        aria-hidden="true"
                        className="h-6 w-6 text-[#8B4AFA]/20 transition-colors duration-300 group-hover:text-[#8B4AFA]/50"
                      />
                    </div>

                    <blockquote className="mt-5 flex-1 text-base leading-7 text-slate-600">
                      {t.before}
                      <mark className="rounded bg-[#E9D5FF]/70 px-1 text-[#4c2a99]">{t.highlight}</mark>
                      {t.after}
                    </blockquote>

                    <figcaption className="mt-6 flex items-center gap-3 border-t border-slate-100 pt-5">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#8B4AFA] text-sm font-bold uppercase text-white ring-0 ring-[#8B4AFA]/20 transition-all duration-300 group-hover:ring-4">
                        {t.name.charAt(0)}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-slate-900">{t.name}</span>
                        <span className="block text-xs text-slate-500">Client review</span>
                      </span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ================================ GALLERY ========================== */}
        <section className="px-4 py-24 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <SectionHeader
              eyebrow="Gallery"
              title="Meet the people behind our success"
              text="The talented individuals who bring passion, creativity, and dedication to every project."
            />

            <div className="mt-14 grid auto-rows-[10rem] grid-cols-2 gap-3 sm:auto-rows-[12rem] sm:gap-4 md:grid-cols-4 lg:auto-rows-[14rem]">
              {GALLERY.map((g, i) => (
                <Reveal
                  key={g.src}
                  delay={(i % 4) * 0.08}
                  amount={0.15}
                  className={g.big ? "col-span-2 row-span-2" : ""}
                >
                 <GalleryImage g={g} priority={i === 0} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ================================ CTA ============================== */}
        <section className="px-4 pb-24 sm:px-6 lg:px-8">
          <Reveal className="mx-auto max-w-5xl" amount={0.3}>
            <div className="relative overflow-hidden rounded-3xl border border-[#8B4AFA]/15 bg-gradient-to-b from-[#F5EFFF] to-white px-6 py-14 text-center shadow-xl shadow-[#8B4AFA]/10 transition-[border-color,box-shadow] duration-300 hover:border-[#8B4AFA]/40 hover:shadow-[#8B4AFA]/20 sm:px-12">
              {/* Soft brand glows */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#8B4AFA]/10 blur-3xl"
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-[#bb94ff]/15 blur-3xl"
              />
              <div className="relative">
                <h2 className="mt-4 font-display text-3xl font-semibold !leading-[1.15] text-slate-900 [text-wrap:balance] sm:text-4xl lg:text-5xl">
                  Ready to start your project?
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-slate-600">
                  Tell us what you need and our team will get back to you shortly.
                </p>
                <div className="mt-8 flex flex-col items-stretch justify-center gap-4 pb-1.5 pr-1.5 sm:flex-row sm:items-center">
                  <BrandButton as={Link} to="/contact" className="w-full sm:w-auto">
                    Start Your Project
                  </BrandButton>
                </div>
              </div>
            </div>
          </Reveal>
        </section>

      </div>
    </MotionConfig>
  );
};

export default About;